import React, { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { userRoles, rolePermissions } from '../../data/mockData'
import { useBranding, brandHex, brandTint, brandInk } from '../../hooks/useBranding.jsx'
import { useSidebar } from '../../hooks/useSidebar.jsx'
import { useProductContext } from '../../hooks/useProductContext.jsx'
import api from '../../services/api'
import { useNavBadges } from '../../hooks/useNavBadges.jsx'
import useFreshOnReturn, { WORK_CHANGED } from '../../hooks/useFresh.js'
import { useTranslation } from 'react-i18next'

function Sidebar() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isMobile, isMobileOpen, isCollapsed, closeMobile, toggle } = useSidebar()
  const { productRole, productName } = useProductContext()
  const permissions = rolePermissions[user?.role] || {}
  // Labels are translated HERE rather than in the ~40 `name:` definitions below, which repeat
  // the same label across role branches. The key IS the English label, so a menu item added
  // later without a translation still reads as English instead of as a dotted key.
  const { t: tNav } = useTranslation('nav')
  const label = (name) => tNav(name, { defaultValue: name })

  // Nav badge dots — colored indicators for unread notifications
  const { badges, markSectionRead } = useNavBadges()

  // Whose name is on this. Ours unless a company is served at this address.
  const branding = useBranding()
  const brandInitials = (branding.productName || 'ProTrack')
    .split(/\s+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase()

  // Whether this person administers a company of their own.
  //
  // Asked rather than read off the user, because it is not a fact about the person — it is a
  // fact about them AT one company, and somebody may administer one and merely work at another.
  // Fetched for everybody because there is no way to know without asking; the endpoint returns
  // only the caller's own memberships, and an empty answer is the common one.
  const [orgAdmin, setOrgAdmin] = useState(false)
  useEffect(() => {
    if (!user) { setOrgAdmin(false); return }
    api.get('/organisations/mine')
      .then(r => setOrgAdmin((r.data?.items || []).some(o => o.is_org_admin)))
      .catch(() => setOrgAdmin(false))
  }, [user])

  // Inside a company: how many documents wait on this person, and how many came back to them,
  // as counts beside Approvals and Sent back to me. Asked again on every page change, after they act
  // on a document (workChanged) and when they come back to this window, so a count falls as soon as
  // the person acts -- and rises as soon as somebody else does.
  const [waiting, setWaiting] = useState(null)
  const askCounts = () => {
    if (!user?.organisation || user?.role === userRoles.PARTNER) { setWaiting(null); return }
    api.get('/approvals/').then(r => setWaiting(r.data || null)).catch(() => setWaiting(null))
  }
  useEffect(askCounts, [user, location.pathname, location.search])
  useFreshOnReturn(askCounts, [WORK_CHANGED])

  // Demo mode flag — only fetched when an Admin is logged in
  const [demoMode, setDemoMode] = useState(false)
  useEffect(() => {
    if (user?.role === userRoles.ADMIN) {
      api.get('/demo/status').then(r => setDemoMode(!!r.data.demo_mode_enabled)).catch(() => setDemoMode(false))
    } else {
      setDemoMode(false)
    }
  }, [user])

  const getNavigationByRole = () => {
    const baseNavigation = [
      {
        name: 'Dashboard',
        href: '/dashboard',
        icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z M9 3v2m6-2v2',
        roles: [userRoles.ADMIN, userRoles.Funder, userRoles.PROJECT_MANAGER, userRoles.FINANCE, userRoles.CONSULTANT, userRoles.HR]
      },
      {
        // v3.4 self-service leave — available to everyone.
        name: 'My Leave',
        href: '/hr/my-leave',
        icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
        roles: [userRoles.ADMIN, userRoles.PROJECT_MANAGER, userRoles.FINANCE, userRoles.CONSULTANT, userRoles.HR]
      },
      {
        // 3 Pillars ID — one login, many products. Everyone gets the Launchpad; it simply shows
        // ProTrack alone until they're granted another product.
        name: 'My Products',
        href: '/products',
        icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z',
        // The ONLY item an external partner sees. Their work lives in the product they were given.
        roles: [userRoles.ADMIN, userRoles.Funder, userRoles.PROJECT_MANAGER, userRoles.FINANCE, userRoles.CONSULTANT, userRoles.HR, userRoles.PARTNER]
      },
      {
        // Anyone can report a bug, so anyone needs to see what happened to it. Reports used to
        // go in and never come back, which is why the same issue arrives three times from three
        // people. Deliberately not under the Admin's "Feedback & Issues" — that is the inbox,
        // this is your own outbox.
        name: 'My Tickets',
        href: '/my-tickets',
        icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
        roles: [userRoles.ADMIN, userRoles.Funder, userRoles.PROJECT_MANAGER, userRoles.FINANCE, userRoles.CONSULTANT, userRoles.HR]
      },
      {
        // The page a new person reads on their first day: who does what, and who approves what.
        // For everybody, so it is also the first answer to "why can't I see that menu?".
        name: 'Roles & Responsibilities',
        href: '/roles',
        icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
        roles: [userRoles.ADMIN, userRoles.Funder, userRoles.PROJECT_MANAGER, userRoles.FINANCE, userRoles.CONSULTANT, userRoles.HR]
      }
    ]

    // A company's teams, each with its people and its dossiers. Only inside a company: our own work
    // keeps its projects.
    const teamsItem = { name: 'Teams', href: '/teams', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' }

    // INSIDE A COMPANY, ITS OWN MENU (plan v2, page 6) -- never ours. Their work is teams and their
    // dossiers; our projects, contracts, onboarding, recycle bin and platform are 3 Pillars' and stay at
    // our address (the server refuses the platform there anyway). A Viewer gets their teams only.
    if (user?.organisation) {
      if (user?.role === userRoles.PARTNER) return [teamsItem]
      const counts = waiting?.counts
      const home = { name: 'Front page', href: '/dashboard', icon: baseNavigation[0].icon }
      const approvals = { name: 'Approvals', href: '/approvals', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
                          count: counts ? counts.approve + counts.agree : 0, countTone: 'amber' }
      const sentBack = { name: 'Sent back to me', href: '/approvals?show=sent_back', count: counts?.sent_back || 0,
                         countTone: 'red', icon: 'M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6' }
      // Their open dossiers, a click away (newest first), as the plan's front page shows them.
      const open = isCollapsed ? [] : (waiting?.dossiers || []).slice(0, 6)
      const dossierItems = open.length ? [{ name: 'Dossiers', section: true },
        ...open.map(d => ({ name: d.title, href: d.link, key: `dossier-${d.id}`, indent: true }))] : []
      // Only for whoever may archive: the administrator, and the records keepers a dossier's rules name.
      const archive = waiting?.keeper ? [{ name: 'Archive & retention', href: '/archive-retention',
                                           icon: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4' }] : []
      const own = baseNavigation.filter(item => item.href === '/my-tickets')
      const org = orgAdmin ? [{ name: 'My Organisation', href: '/my-organisation',
                                icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' }] : []
      return [home, approvals, sentBack, teamsItem, ...dossierItems, ...archive, ...org, ...own]
    }

    // An external partner gets the Launchpad and nothing else — no Dashboard, no Leave, no
    // projects. Returning early keeps that guarantee in one place instead of relying on every
    // future nav item remembering to exclude them.
    if (user?.role === userRoles.PARTNER) {
      return baseNavigation.filter(item => item.href === '/products')
    }

    // Project templates — visible to whoever plans work, since they are what a new project
    // starts from. Changing one needs the capability the server checks; looking is open.
    if (user?.role === userRoles.ADMIN || user?.role === userRoles.PROJECT_MANAGER) {
      baseNavigation.push({
        name: 'Templates',
        href: '/templates',
        icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
        roles: [userRoles.ADMIN, userRoles.PROJECT_MANAGER]
      })
    }

    // Their own company's settings — shown only to somebody who administers one. This is a
    // customer's screen, not ours, so it is not tied to being a ProTrack Admin.
    if (orgAdmin) {
      baseNavigation.push({
        name: 'My Organisation',
        href: '/my-organisation',
        icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
        roles: [userRoles.ADMIN, userRoles.Funder, userRoles.PROJECT_MANAGER, userRoles.FINANCE, userRoles.CONSULTANT, userRoles.HR, userRoles.PARTNER]
      })
    }

    // The platform panel is for whoever manages the platform itself — a Super Admin, or a
    // ProTrack Admin (who can create the first Super Admin; there has to be a way in). The
    // endpoints re-check this server-side, so hiding it here is convenience, not security.
    if (user?.is_super_admin || user?.role === userRoles.ADMIN) {
      // The recycle bin sits with the admin tools rather than beside Projects: it spans
      // projects, users, milestones, deliverables and documents, so it belongs to none of them.
      // An Admin already has it in their own admin list below; this one is for a Super Admin
      // who is not also an Admin, so an Admin does not see it twice.
      if (user?.role !== userRoles.ADMIN) {
        baseNavigation.push({
          name: 'Recycle Bin',
          href: '/admin/recycle-bin',
          icon: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
          roles: [userRoles.ADMIN, userRoles.Funder, userRoles.PROJECT_MANAGER, userRoles.FINANCE, userRoles.CONSULTANT, userRoles.HR]
        })
      }
      baseNavigation.push({
        name: 'Platform',
        href: '/platform',
        icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
        roles: [userRoles.ADMIN, userRoles.Funder, userRoles.PROJECT_MANAGER, userRoles.FINANCE, userRoles.CONSULTANT, userRoles.HR]
      })
    }

    // Every stored file and whether anything uses it. Super Admin only, unlike the recycle bin:
    // the bucket is shared by every company and by staging, so it belongs to whoever runs the
    // platform. The server refuses anybody else regardless.
    if (user?.is_super_admin) {
      baseNavigation.push({
        name: 'File Manager',
        href: '/admin/file-manager',
        icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
        roles: [userRoles.ADMIN, userRoles.Funder, userRoles.PROJECT_MANAGER, userRoles.FINANCE, userRoles.CONSULTANT, userRoles.HR]
      })
    }

    const roleSpecificNavigation = []

    if (user?.role === userRoles.ADMIN) {
      roleSpecificNavigation.push(
        { name: 'Projects', href: '/projects', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
        { name: 'Onboarding', href: '/onboarding', icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z' },
        { name: 'People & Access', href: '/admin/people', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
        { name: 'Personal Data Export', href: '/admin/personal-data-export', icon: 'M12 10v6m0 0l-3-3m3 3l3-3M6 20h12a2 2 0 002-2V8l-6-6H6a2 2 0 00-2 2v14a2 2 0 002 2z' },
        // Spans projects, users, milestones, deliverables and documents, so it sits with the
        // admin tools rather than beside any one of them.
        { name: 'Recycle Bin', href: '/admin/recycle-bin', icon: 'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16' },
        // Where every archived project and legal hold can be seen at once; the purge itself stays on the project.
        { name: 'Archive & Retention', href: '/admin/archive', icon: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4' },
        { name: 'Feedback & Issues', href: '/admin/feedback', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' },
        { name: 'Contracts', href: '/contracts', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
        { name: 'My Team', href: '/my-team', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
        { name: 'User Management', href: '/team/management', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
        { name: 'Consultants', href: '/consultants', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
        { name: 'Invoice Queue', href: '/finance/invoices', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
        { name: 'Budget Tracking', href: '/finance/budget', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1' },
        { name: 'Financial Reports', href: '/finance/reports', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' }
      )
      // Demo / Testing link — only shown when backend reports DEMO_MODE=true
      if (demoMode) {
        roleSpecificNavigation.push({
          name: '🔄 Demo / Testing',
          href: '/demo',
          icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
        })
      }
    }

    if (user?.role === userRoles.Funder) {
      roleSpecificNavigation.push(
        { name: 'Projects', href: '/projects', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
        { name: 'Funding Approval', href: '/funding/approval', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
        { name: 'Financial Reports', href: '/finance/reports', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' }
      )
    }

    if (user?.role === userRoles.PROJECT_MANAGER) {
      roleSpecificNavigation.push(
        { name: 'Projects', href: '/projects', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
        // Onboarding pipeline is Admin-only — removed from PM nav (it was a dead link that
        // funnelled PMs into the consultant "My Onboarding" flow).
        { name: 'Contracts', href: '/contracts', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
        { name: 'Budget Tracking', href: '/finance/budget', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1' },
        { name: 'My Team', href: '/my-team', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
        { name: 'Consultants', href: '/consultants', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' }
      )
    }

    if (user?.role === userRoles.FINANCE) {
      roleSpecificNavigation.push(
        { name: 'Projects', href: '/projects', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
        { name: 'Contracts', href: '/contracts', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
        { name: 'Invoice Queue', href: '/finance/invoices', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
        { name: 'Financial Reports', href: '/finance/reports', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
        { name: 'Budget Tracking', href: '/finance/budget', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1' },
        { name: 'My Team', href: '/my-team', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' }
      )
    }

    if (user?.role === userRoles.CONSULTANT) {
      roleSpecificNavigation.push(
        { name: 'My Projects', href: '/consultant/projects', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' },
        { name: 'My Contracts', href: '/contracts', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
        { name: 'My Team', href: '/my-team', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
        { name: 'My Onboarding', href: '/onboarding/my', icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z' }
        // Disputes intentionally hidden from Consultant nav — Admin-only workflow
      )
    }

    // v3.3 HR role: people/onboarding/compliance oversight. The HR dashboard itself is
    // self-contained; Onboarding + Contracts are read surfaces HR can use.
    if (user?.role === userRoles.HR) {
      roleSpecificNavigation.push(
        { name: 'People Ops', href: '/hr/people-ops', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
        { name: 'Onboarding', href: '/onboarding', icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z' },
        // HR screens candidates, so they need the consultant directory + CV downloads.
        { name: 'Consultants', href: '/consultants', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
        { name: 'Contracts', href: '/contracts', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' },
      )
    }

    // Disputes + Legal — ADMIN ONLY, grouped at the very bottom.
    // (Other roles don't see Legal; they ask the Admin via chat/email and the
    //  Admin shares complaint/access links, which arrive by email.)
    if (user?.role === userRoles.ADMIN) {
      roleSpecificNavigation.push(
        { name: 'Disputes', href: '/disputes', icon: 'M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3' },
        { name: 'Legal', section: true },
        { name: 'Register Complaints', href: '/legal/complaints', icon: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z' },
        { name: 'Give Access', href: '/legal/access', icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z' },
      )
    }

    // Delegated capabilities (v2): a non-admin who was granted admin-style capabilities gets
    // the matching nav links, on top of their role's normal menu. Avoid duplicating links a
    // role already has.
    if (user?.role !== userRoles.ADMIN) {
      const caps = user?.capabilities || []
      const names = new Set(roleSpecificNavigation.map(n => n.name))
      const addOnce = (item) => { if (!names.has(item.name)) { roleSpecificNavigation.push(item); names.add(item.name) } }
      if (caps.includes('onboard_users')) {
        addOnce({ name: 'Onboarding', href: '/onboarding', icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z' })
      }
      // Somebody who fixes tickets needs the LIST, not only their own assigned items —
      // to spot duplicates and pick up neighbouring work. They still cannot assign.
      if (caps.includes('handle_tickets')) {
        addOnce({ name: 'Feedback & Issues', href: '/admin/feedback', icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z' })
      }
      if (caps.includes('manage_users')) {
        addOnce({ name: 'People & Access', href: '/admin/people', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' })
      }
      if (caps.includes('export_personal_data')) {
        addOnce({ name: 'Personal Data Export', href: '/admin/personal-data-export', icon: 'M12 10v6m0 0l-3-3m3 3l3-3M6 20h12a2 2 0 002-2V8l-6-6H6a2 2 0 00-2 2v14a2 2 0 002 2z' })
      }
      if (caps.includes('manage_all_projects')) {
        addOnce({ name: 'Projects', href: '/projects', icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10' })
      }
      if (caps.includes('manage_finance')) {
        addOnce({ name: 'Invoice Queue', href: '/finance/invoices', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' })
        addOnce({ name: 'Budget Tracking', href: '/finance/budget', icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1' })
      }
    }

    return [...baseNavigation, ...roleSpecificNavigation]
  }

  // Which nav items belong to an optional module. Matched on the address rather than tagged on
  // every item, because the items are built in a dozen places and one added later would quietly
  // miss a tag — whereas a new /finance/… screen is caught by the prefix without anyone
  // remembering. The API refuses these regardless; this only stops us offering a door that does
  // not open.
  //
  // Deliberately NOT listed: /products and /platform. Those are 3 Pillars ID — the launchpad and
  // the platform panel — not the "Connected products" module a customer buys.
  const MODULE_BY_PREFIX = [
    ['/hr', 'hr'],
    ['/finance', 'finance'],
    ['/legal', 'legal'],
    ['/disputes', 'legal'],
    ['/funding', 'funder'],

    ['/contracts', 'projects'],

    ['/consultants', 'projects'],

    ['/finance/invoices', 'projects'],
  ]

  // Absent or unknown means on. A company nobody has configured, and our own installation, keep
  // everything — the same default the server applies, so the two cannot disagree.
  const modules = user?.modules || {}
  const moduleAllows = (href = '') => {
    const hit = MODULE_BY_PREFIX.find(([prefix]) => href === prefix || href.startsWith(prefix + '/'))
    return !hit || modules[hit[1]] !== false
  }

  const navigation = getNavigationByRole().filter(item => moduleAllows(item.href))

  // An item whose address carries a query -- "Sent back to me" (/approvals?show=sent_back), a dossier
  // (/teams/1?dossier=1) -- is the one lit while that query is on the page, not Approvals or Teams,
  // which share its path. Every other item lights by its path, as before.
  const here = new URLSearchParams(location.search)
  const litByQuery = navigation.find(item => {
    const [path, query] = (item.href || '').split('?')
    return query && location.pathname === path && [...new URLSearchParams(query)].every(([k, v]) => here.get(k) === v)
  })

  // Sidebar width classes
  const SidebarWidth = isCollapsed ? 'w-20' : 'w-64'

  // Handle link click on mobile
  const handleLinkClick = () => {
    if (isMobile) {
      closeMobile()
    }
  }

  const SidebarContent = (
    <div className={`bg-white ${SidebarWidth} h-full shadow-lg border-r border-gray-200 flex flex-col transition-all duration-300`}>
      {/* header — white strip so the original-colour logo reads naturally */}
      <div className="relative flex items-center justify-center h-16 border-b border-gray-200 bg-white px-4">
        {!isCollapsed && (
          <>
            {/* Original-colour company logo, centred — falls back to text if missing */}
            <img
              src={branding.logoUrl}
              alt={branding.productName}
              className="h-10 object-contain"
              style={{ maxWidth: '180px' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none'
                e.currentTarget.nextElementSibling.style.display = 'block'
              }}
            />
            <h1 className="text-xl font-bold text-blue-700" style={{ display: 'none' }}>{branding.productName}</h1>
          </>
        )}
          {/* Their initials while the sidebar is collapsed, not ours. */}
        {isCollapsed && (
          <h1 className="text-xl font-bold text-blue-700 mx-auto">{brandInitials}</h1>
        )}
        {!isMobile && (
          <button
            onClick={toggle}
            className="absolute right-2 p-1 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-700 transition-colors"
            aria-label={isCollapsed ? 'Expand Sidebar' : 'Collapse Sidebar'}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isCollapsed ? 'M13 5l7 7-7 7M5 5l7 7-7 7' : 'M11 19l-7-7 7-7m8 14l-7-7 7-7'} />
            </svg>
          </button>
        )}
      </div>

      {/* Navigation */}
      <div className="grow overflow-y-auto">
        <nav className="mt-4">
          {navigation.map((item) => {
            // Section header (e.g. "Legal") — a non-clickable divider label
            if (item.section) {
              if (isCollapsed) {
                return <div key={item.name} className="my-2 mx-3 border-t border-gray-200" />
              }
              return (
                <p key={item.name} className="px-6 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                  {label(item.name)}
                </p>
              )
            }

            const isActive = litByQuery ? item === litByQuery : (location.pathname === item.href ||
              (item.href !== '/dashboard' && location.pathname.startsWith(item.href)))

            // Find badge for this nav item
            const badgePrefix = Object.keys(badges).find(prefix =>
              item.href === prefix || item.href.startsWith(prefix) || prefix.startsWith(item.href)
            )
            const badge = badgePrefix ? badges[badgePrefix] : null
            const badgeColor = badge?.color
            const badgeLink = badge?.link  // specific URL to navigate to

            const dotColors = { green: 'bg-green-500', red: 'bg-red-500', amber: 'bg-amber-400' }
            const dotClass = badgeColor ? dotColors[badgeColor] || 'bg-gray-400' : null

            return (
              <Link
                key={item.key || item.name}
                to={badgeLink && !isActive ? badgeLink : item.href}
                onClick={() => {
                  handleLinkClick()
                  if (badgePrefix) markSectionRead(badgePrefix)
                }}
                className={`flex items-center ${isCollapsed ? 'justify-center px-3 py-3 text-sm' : item.indent ? 'pl-12 pr-6 py-1.5 text-[13px]' : 'px-6 py-3 text-sm'} font-medium transition-colors duration-150 ${isActive
                  ? 'text-blue-600 bg-blue-50 border-r-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                // A company's own colour on the item they are looking at — the clearest single
                // place the product reads as theirs. Inline so it overrides the classes above
                // without a second set of them, and applied ONLY when a colour was chosen, so
                // ours is untouched and every other state keeps its existing meaning.
                style={isActive && brandHex(branding.primaryColour) ? {
                  // Darkened for the text, full strength for the edge. A pale colour as text on
                  // its own faint wash is invisible; as a 2px border against white it is fine.
                  color: brandInk(branding.primaryColour),
                  borderRightColor: branding.primaryColour,
                  backgroundColor: brandTint(branding.primaryColour),
                } : undefined}
                title={isCollapsed ? label(item.name) : (badge?.title || undefined)}
              >
                {item.indent ? <span className="w-1.5 h-1.5 rounded-full bg-gray-300 shrink-0 mr-3" aria-hidden="true" /> : (
                <svg className={`w-5 h-5 shrink-0 ${isCollapsed ? '' : 'mr-3'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                </svg>)}

                {!isCollapsed && (
                  <span className="flex-1 flex items-center justify-between">
                    {label(item.name)}
                    {item.count > 0 && (
                      <span className={`ml-2 min-w-[1.25rem] px-1.5 rounded-full text-[11px] font-semibold text-center ${
                        item.countTone === 'amber' ? 'bg-amber-400 text-gray-900' : item.countTone === 'red' ? 'bg-red-100 text-red-700' : 'bg-blue-600 text-white'}`}>{item.count}</span>
                    )}
                    {/* Single dot on right side only — no duplicate on icon */}
                    {dotClass && !isActive && (
                      <span className={`w-2.5 h-2.5 rounded-full ${dotClass} shrink-0 animate-pulse`} />
                    )}
                  </span>
                )}

                {/* Collapsed sidebar: dot goes on top-right of icon */}
                {isCollapsed && dotClass && !isActive && (
                  <span className={`absolute top-2 right-2 w-2 h-2 rounded-full ${dotClass}`} />
                )}
              </Link>
            )
          })}
        </nav>
      </div>

      {/* Footer */}
      {!isCollapsed && (
        <div className="p-4">
          <div className="bg-linear-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-blue-900">
                  {productRole ? `Your role in ${productName}` : 'Role Access'}
                </p>
                {/* Falls back to the ProTrack role under the 'Role Access' heading, never under
                    one naming the product — that heading would make it read as theirs there. */}
                <p className="text-xs text-blue-700">{productRole || user?.display_role || user?.role}</p>
              </div>
              <div className="h-2 w-2 bg-green-400 rounded-full"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  )

  // Mobile: Overlay Sidebar
  if (isMobile) {
    return (
      <>
        {/* Mobile overlay */}
        {isMobileOpen && (
          <div
            className="fixed inset-0 bg-gray-900/50 z-40 lg:hidden"
            onClick={closeMobile}
          />
        )}

        {/* Mobile Sidebar */}
        <div className={`fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out lg:hidden ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'
          }`}>
          {SidebarContent}
        </div>
      </>
    )
  }

  // desktop: Static Sidebar
  return SidebarContent
}

export default Sidebar
